import React, { useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { ArrowLeft, UserPlus, Hash, User } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { API, pageVariants } from './utils';
import { useGuestPortal } from './GuestPortalContext';

export function RegisterForm({ setView }) {
  const { t } = useGuestPortal();
  const [employeeNumber, setEmployeeNumber] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  const handleRegister = async () => {
    if (!employeeNumber || !name) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/guests/register`, {
        employee_number: employeeNumber,
        name
      });
      toast.success("Registration successful! You can now check in.");
      setView("menu");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      className="w-full max-w-md"
    >
      <Card className="bg-vault-surface border border-vault-border shadow-xl p-8 rounded-2xl" data-testid="register-form-card">
        <CardHeader className="pb-6">
          <button 
            onClick={() => setView("menu")} 
            className="text-gray-500 hover:text-vault-gold transition-colors mb-4 flex items-center gap-2"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <CardTitle className="font-outfit text-2xl font-bold text-vault-text tracking-tight flex items-center gap-3">
            <UserPlus className="w-6 h-6 text-amber-500" />
            Guest Registration
          </CardTitle>
          <p className="text-vault-text-secondary text-sm mt-2">
            Register with your employee details. Signature will be captured at check-in.
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <label className="vault-label">Employee Number</label>
            <div className="relative">
              <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-vault-gold" />
              <Input
                value={employeeNumber}
                onChange={(e) => setEmployeeNumber(e.target.value)}
                placeholder="⬇️ TAP HERE to enter number"
                className="pl-10 vault-input input-highlight text-lg"
                data-testid="employee-number-input"
                autoFocus
              />
            </div>
          </div>
          <div>
            <label className="vault-label">Full Name</label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-vault-gold" />
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="⬇️ TAP HERE to enter name"
                className="pl-10 vault-input text-lg"
                data-testid="name-input"
              />
            </div>
          </div>
          <Button
            onClick={handleRegister}
            disabled={loading}
            className="w-full vault-btn-primary h-12"
            data-testid="submit-register-btn"
          >
            {loading ? "Registering..." : "Complete Registration"}
          </Button>
        </CardContent>
      </Card>
    </motion.div>
  );
}
